import { useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { useTaskProcessor } from '@eluve/appointment-tasks';
import {
  Button,
  Icon,
  Label,
  Stack,
  Textarea,
  useToast,
} from '@eluve/components';
import { insertTranscriptMutation } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { usePeriodicRemoteFileSyncSelector } from '@eluve/user-local-files';

import { TranscriptLibraryDialog } from './TranscriptLibraryDialog';

interface AddToTranscriptProps {
  appointmentId: string;
}

export const AddToTranscript: React.FC<AddToTranscriptProps> = ({
  appointmentId,
}) => {
  const toast = useToast();
  const logger = useNamedLogger('AddToTranscript');
  const [insertTranscript, { loading }] = useMutation(insertTranscriptMutation);
  const [transcript, setTranscript] = useState('');
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const tenantId = useTenantIdFromParams();
  const { taskProcessor } = useTaskProcessor();

  const hasPendingAppointmentTask = taskProcessor
    ?.getTasks()
    .some((t) => t.appointmentId === appointmentId);

  const hasPendingAppointmentUploadTasks = usePeriodicRemoteFileSyncSelector(
    (s) =>
      s.context.fileQueue?.some((q) => q.appointmentId === appointmentId) ||
      s.context.currentFile?.appointmentId === appointmentId,
  );

  const hasPendingTasks =
    hasPendingAppointmentTask || hasPendingAppointmentUploadTasks;

  const disabled = useMemo(
    () => !transcript.trim() || loading,
    [transcript, loading],
  );

  const addTranscriptToAppointment = async () => {
    if (!transcript) {
      return;
    }

    const segmentId = uuid();

    const { errors } = await insertTranscript({
      context: eluveAdminHasuraContext,
      variables: {
        tenantId: tenantId ?? '',
        appointmentId: appointmentId,
        transcript: transcript,
        segmentId,
      },
    });

    if (errors) {
      logger.error('Failed to upload transcript');
      toast.error('Failed to upload transcript');
      return;
    }

    setTranscript('');
  };

  return (
    <Stack fullWidth={false} gap={2}>
      <Label className="px-2">Transcript</Label>
      <Textarea
        className="h-[70px] w-full resize-none text-text-primary outline-hidden focus-visible:ring-0"
        value={transcript ?? ''}
        placeholder="Paste transcript here..."
        onChange={(e) => setTranscript(e.target.value)}
      />
      <Stack gap={2} direction="horizontal">
        <Button
          disabled={disabled || hasPendingTasks}
          onClick={addTranscriptToAppointment}
        >
          Add to transcript
        </Button>
        <Button variant="outline" onClick={() => setIsLibraryOpen(true)}>
          <Icon className="mr-2" name="BookCopy" size="xs" />
          ...or pick from the library
        </Button>
      </Stack>
      <TranscriptLibraryDialog
        open={isLibraryOpen}
        onOpenChange={setIsLibraryOpen}
        onUseTranscript={setTranscript}
      />
    </Stack>
  );
};
